import { useMutation, useQueryClient } from '@tanstack/react-query'
import { differenceInCalendarDays } from 'date-fns'
import { useCallback, useMemo } from 'react'
import {
  allMonthCalendarQueries,
  readMonthCalendar,
  type MonthCalendarKey,
} from '@opallabs/api/BFF'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { ActionToast, toast } from '@opallabs/lapidary'
import { buildDropProposal } from '../dnd'
import { parseCalendarDate } from '../monthGrid'
import { indexItemsById } from '../resolveItemColor'
import {
  type CalendarItem,
  type CalendarItemEdit,
  type OnCalendarItemEdit,
} from '../types'
import { renameContent, renameMoment } from './itemActions'
import {
  endDateOf,
  rescheduleContentTakedown,
  rescheduleMoment,
} from './reschedule'
import { useRescheduleCalendarItem } from './useRescheduleCalendarItem'

type Rename = { item: CalendarItem; title: string }

/**
 * New dates for a moment. `end` is inclusive, as it is on `CalendarItem`.
 *
 * `from` is the day the moment starts on now. Undo cannot read that off the
 * item, which is the one the calendar drew before the edit was made.
 */
type MomentDates = {
  end: string
  from: string
  item: CalendarItem
  start: string
}

/** The last day a piece of content is in market. `yyyy-MM-dd`. */
type ContentEnd = { date: string; item: CalendarItem }

export const useEditCalendarItem = ({
  identifiers,
  items,
  locale,
}: {
  /** The month window on screen, so its cache can be written into. */
  identifiers: MonthCalendarKey
  /**
   * Everything in view. Content belongs to a moment, and only the moment it
   * belongs to says how early the content may go up.
   */
  items: readonly CalendarItem[]
  locale?: IETFLanguageTag
}): OnCalendarItemEdit => {
  const queryClient = useQueryClient()
  const itemsById = useMemo(() => indexItemsById(items), [items])
  const reschedule = useRescheduleCalendarItem({ identifiers, locale })

  const refresh = useCallback(
    () =>
      allMonthCalendarQueries.invalidateCache(queryClient, {
        workspaceUuid: identifiers.workspaceUuid,
      }),
    [identifiers.workspaceUuid, queryClient]
  )

  const { mutate: rename } = useMutation({
    mutationFn: async ({ item, title }: Rename) => {
      if (item.kind === 'moment') {
        await renameMoment({ momentId: item.id, title })
        return
      }
      await renameContent({ item, name: title })
    },

    onMutate: ({ item, title }) => {
      readMonthCalendar.setQueryData(
        queryClient,
        identifiers,
        readMonthCalendar.transformCache.renameItem(item.id, {
          kind: item.kind === 'moment' ? 'moment' : 'content',
          name: title,
        })
      )
    },

    onError: () => {
      toast('That could not be renamed. Please try again.', { type: 'error' })
    },

    onSettled: refresh,
  })

  /**
   * A moment's own dates, each set on its own. Neither is a move of the kind a
   * drag makes — one end stays put while the other is put somewhere new — so
   * this cannot go through the drop proposal the grid builds.
   */
  const { mutate: redate } = useMutation({
    mutationFn: ({ item, start, ...dates }: MomentDates) => {
      const from = parseCalendarDate(dates.from, {
        field: 'start',
        id: item.id,
      })

      return rescheduleMoment({
        days:
          from === null
            ? 0
            : differenceInCalendarDays(
                parseCalendarDate(start, { field: 'start', id: item.id }) ??
                  from,
                from
              ),
        endDate: endDateOf({ end: dates.end, start }),
        momentId: item.id,
      })
    },

    onMutate: (dates) => {
      readMonthCalendar.setQueryData(
        queryClient,
        identifiers,
        readMonthCalendar.transformCache.moveMoment(dates.item.id, {
          endDate: endDateOf(dates),
          startDate: dates.start,
        })
      )
    },

    onError: () => {
      toast('That could not be moved. Please try again.', { type: 'error' })
    },

    onSettled: refresh,
  })

  /**
   * The day a piece of content comes down. Its own write, and not a move: the
   * day the content goes up stays where it is, which is what the drag path
   * could never do.
   */
  const { mutate: redateTakedown } = useMutation({
    mutationFn: ({ date, item }: ContentEnd) =>
      rescheduleContentTakedown({ contentId: item.id, date }),

    onMutate: ({ date, item }) => {
      readMonthCalendar.setQueryData(queryClient, identifiers, (draft) => {
        const cached = draft.data.content.by_id[item.id]
        if (cached !== undefined) cached.takedownDate = date
        return draft
      })
    },

    onError: () => {
      toast('That could not be moved. Please try again.', { type: 'error' })
    },

    onSettled: refresh,
  })

  return useCallback(
    (edit: CalendarItemEdit) => {
      if (edit.field === 'title') {
        rename({ item: edit.item, title: edit.title })
        return
      }

      const { date, item } = edit

      // A piece of content comes down when its own takedown says so, and the
      // day it goes up stays where it is.
      if (item.kind !== 'moment' && edit.field === 'endDate') {
        // Content cannot come down before it goes up, the way a moment cannot
        // end before it starts: nothing is written and nothing moves.
        if (date < item.start || date === item.inMarket?.end) return

        redateTakedown({ date, item })
        return
      }

      // Content keeps its time of day and its in-market window, which is what
      // a whole-day shift means — so a panel edit takes the drag's own path.
      if (item.kind !== 'moment') {
        const targetDate = parseCalendarDate(date, {
          field: 'date',
          id: item.id,
        })
        if (targetDate === null) return

        const proposal = buildDropProposal({
          item,
          parent:
            item.parentId === undefined
              ? undefined
              : itemsById.get(item.parentId),
          targetDate,
        })
        // Content that would go up before its moment is refused the same way a
        // drag onto that day is: nothing is written and nothing moves.
        if (proposal === null || proposal.forbiddenReason !== null) return

        reschedule(proposal)
        return
      }

      const dates =
        edit.field === 'startDate'
          ? { end: item.end, from: item.start, item, start: date }
          : { end: date, from: item.start, item, start: item.start }
      if (dates.start === item.start && dates.end === item.end) return

      redate(dates, {
        onSuccess: () => {
          const previous = {
            end: item.end,
            from: dates.start,
            item,
            start: item.start,
          }
          toast(
            (toastProps) => (
              <ActionToast
                {...toastProps}
                actionText="Undo"
                actionType="secondary"
                message={`"${item.title}" now runs ${readableRange(dates, locale)}.`}
                title="Rescheduled"
                onActionClick={() => redate(previous)}
              />
            ),
            { autoClose: 8000, type: 'success' }
          )
        },
      })
    },
    [itemsById, locale, redate, redateTakedown, rename, reschedule]
  )
}

/** "3 August – 9 August", or one day when a moment lasts only one. */
const readableRange = (
  { end, start }: MomentDates,
  locale: IETFLanguageTag | undefined
): string => {
  const day = (isoDate: string) =>
    new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(
      new Date(`${isoDate}T00:00:00`)
    )

  return start === end ? day(start) : `${day(start)} – ${day(end)}`
}
